/**
 * Serviço para persistência de contexto de sessão em localStorage
 */

export interface SessionContext {
  networkId: string | null;
  storeId: string | null;
  contextMode: 'NETWORK' | 'STORE';
  actingAsStoreId: string | null;
  timestamp: number;
}

interface UserContextHistory {
  userId: string;
  lastContexts: SessionContext[];
  currentContext: SessionContext | null;
}

const STORAGE_KEY = 'autofluxo_session_context';
const HISTORY_KEY = 'autofluxo_context_history';
const MAX_HISTORY = 5;

/**
 * Salva o contexto atual no localStorage
 */
export const saveSessionContext = (context: SessionContext): void => {
  try {
    const contextWithTimestamp = {
      ...context,
      timestamp: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(contextWithTimestamp));
    
    console.log('💾 SessionStorage - Contexto salvo:', {
      networkId: context.networkId,
      storeId: context.storeId,
      contextMode: context.contextMode,
    });
  } catch (error) {
    console.error('Erro ao salvar contexto da sessão:', error);
  }
};

/**
 * Carrega o contexto da sessão do localStorage
 */
export const loadSessionContext = (): SessionContext | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const context = JSON.parse(stored) as SessionContext;
    
    // Verificar se o contexto não está muito antigo (mais de 7 dias)
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    if (context.timestamp < sevenDaysAgo) {
      clearSessionContext();
      return null;
    }

    return context;
  } catch (error) {
    console.error('Erro ao carregar contexto da sessão:', error);
    return null;
  }
};

/**
 * Limpa o contexto da sessão
 */
export const clearSessionContext = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    // Limpar também sessionStorage legado caso exista
    try { sessionStorage.removeItem('lovable_session_context'); } catch { /* ignore */ }
  } catch (error) {
    console.error('Erro ao limpar contexto da sessão:', error);
  }
};

/**
 * Salva um contexto no histórico do usuário
 */
export const saveToContextHistory = (userId: string, context: SessionContext): void => {
  try {
    const history = loadContextHistory(userId);
    
    // Adicionar novo contexto no início
    history.lastContexts.unshift(context);
    
    // Manter apenas os últimos MAX_HISTORY contextos únicos
    const uniqueContexts = history.lastContexts.filter((ctx, index, self) => 
      index === self.findIndex(c => 
        c.networkId === ctx.networkId && 
        c.storeId === ctx.storeId &&
        c.contextMode === ctx.contextMode
      )
    ).slice(0, MAX_HISTORY);
    
    history.lastContexts = uniqueContexts;
    history.currentContext = context;
    
    localStorage.setItem(`${HISTORY_KEY}_${userId}`, JSON.stringify(history));
  } catch (error) {
    console.error('Erro ao salvar histórico de contexto:', error);
  }
};

/**
 * Carrega o histórico de contextos do usuário
 */
export const loadContextHistory = (userId: string): UserContextHistory => {
  try {
    const stored = localStorage.getItem(`${HISTORY_KEY}_${userId}`);
    if (!stored) {
      return {
        userId,
        lastContexts: [],
        currentContext: null,
      };
    }
    
    return JSON.parse(stored) as UserContextHistory;
  } catch (error) {
    console.error('Erro ao carregar histórico de contexto:', error);
    return {
      userId,
      lastContexts: [],
      currentContext: null,
    };
  }
};

/**
 * Obtém o último contexto válido do usuário
 */
export const getLastValidContext = (userId: string): SessionContext | null => {
  try {
    const history = loadContextHistory(userId);
    return history.currentContext || (history.lastContexts[0] ?? null);
  } catch (error) {
    console.error('Erro ao obter último contexto válido:', error);
    return null;
  }
};

/**
 * Limpa o histórico de contextos do usuário
 */
export const clearContextHistory = (userId: string): void => {
  try {
    localStorage.removeItem(`${HISTORY_KEY}_${userId}`);
  } catch (error) {
    console.error('Erro ao limpar histórico de contexto:', error);
  }
};
